import React, { useState, useEffect } from 'react';
import QrScanner from 'react-qr-scanner';
import api from '../services/api';
import { motion } from 'framer-motion';

export default function BorrowingTab() {
  const [mode, setMode] = useState('manual'); // 'manual' | 'scan'
  const [users, setUsers] = useState([]);
  const [booksList, setBooksList] = useState([]);
  const [records, setRecords] = useState([]);

  // إدخال يدوي
  const [manualUser, setManualUser] = useState('');
  const [manualBook, setManualBook] = useState('');

  // المسح
  const [scanUser, setScanUser] = useState('');
  const [scanBooks, setScanBooks] = useState([]);

  // الأيام/التاريخ
  const [days, setDays] = useState(7);
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().split('T')[0];
  });

  // عندما تتغير الأيام: حدّث تاريخ الإرجاع
  useEffect(() => {
    const d = new Date();
    const n = Number(days) || 1;
    d.setDate(d.getDate() + n);
    setDueDate(d.toISOString().split('T')[0]);
  }, [days]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, bookRes, borrowRes] = await Promise.all([
        api.get('/users'),
        api.get('/books'),
        api.get('/borrowings'),
      ]);
      setUsers(userRes.data || []);
      setBooksList(bookRes.data || []);
      setRecords(borrowRes.data || []);
    } catch (err) {
      console.error(err);
      alert('❌ فشل في تحميل البيانات');
    }
  };

  const handleDateChange = (e) => {
    const val = e.target.value;
    setDueDate(val);
    const today = new Date();
    const target = new Date(val);
    const ms = target - today;
    const diff = Math.max(1, Math.ceil(ms / (1000 * 60 * 60 * 24)));
    setDays(diff);
  };

  const handleManualBorrow = async (e) => {
    e.preventDefault();
    try {
      await api.post('/borrowings', {
        user: manualUser,
        book: manualBook,
        dueDate,
      });
      setManualUser('');
      setManualBook('');
      fetchData();
      alert('✅ تمت الاستعارة بنجاح');
    } catch (err) {
      console.error(err);
      alert('❌ فشل في عملية الاستعارة');
    }
  };

  const handleScan = (result) => {
    if (!result) return;
    const code = result.text || result;

    // أول مسحة: نعتبرها مستخدم
    if (!scanUser) {
      setScanUser(code);
      return;
    }

    // بعدها: مسحات الكتب
    const matchedBook = booksList.find((b) => b._id === code || b.barcode === code);
    if (!matchedBook) {
      alert('⚠️ لم يتم العثور على هذا الكتاب');
      return;
    }
    setScanBooks((prev) => (prev.some((b) => b._id === matchedBook._id) ? prev : [...prev, matchedBook]));
  };

  const handleConfirmScan = async () => {
    if (!scanUser || scanBooks.length === 0) {
      alert('⚠️ يجب تحديد المستخدم وإضافة كتب');
      return;
    }
    try {
      for (const book of scanBooks) {
        await api.post('/borrowings', {
          user: scanUser,
          book: book._id,
          dueDate,
        });
      }
      alert('✅ تمت الاستعارة بنجاح');
      setScanUser('');
      setScanBooks([]);
      fetchData();
    } catch (err) {
      console.error(err);
      alert('❌ فشل في تأكيد الاستعارة');
    }
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا السجل؟')) return;
    try {
      await api.delete(`/borrowings/${id}`);
      fetchData();
      alert('🗑️ تم حذف السجل بنجاح');
    } catch (err) {
      console.error(err);
      alert('❌ فشل في حذف السجل');
    }
  };

  // قيود كاميرا مرنة (اختياري: يفضّل الكاميرا الخلفية على الجوال)
  const previewStyle = { width: '100%', height: '100%', objectFit: 'cover' };
  const videoConstraints = {
    facingMode: 'environment',
  };

  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="container mx-auto max-w-6xl px-4 pt-4"
      dir="rtl"
    >
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">📥 واجهة الاستعارة</h2>

      {/* مبدّل الوضع */}
      <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-6">
        {['manual', 'scan'].map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`px-5 sm:px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
              mode === m
                ? 'bg-blue-700 text-white shadow-md scale-105'
                : 'bg-gray-200 text-gray-700 hover:bg-blue-100'
            }`}
          >
            {m === 'manual' ? '📝 إدخال يدوي' : '📷 المسح بالكود'}
          </button>
        ))}
      </div>

      {/* إدخال يدوي */}
      {mode === 'manual' && (
        <motion.form
          onSubmit={handleManualBorrow}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white p-6 rounded-xl shadow mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <select
            value={manualUser}
            onChange={(e) => setManualUser(e.target.value)}
            className="border p-2 rounded w-full"
            required
          >
            <option value="">👤 اختر المستخدم</option>
            {users.map((u) => (
              <option key={u._id} value={u._id}>
                {u.name}
              </option>
            ))}
          </select>

          <select
            value={manualBook}
            onChange={(e) => setManualBook(e.target.value)}
            className="border p-2 rounded w-full"
            required
          >
            <option value="">📖 اختر الكتاب</option>
            {booksList.map((b) => (
              <option key={b._id} value={b._id}>
                {b.name}
              </option>
            ))}
          </select>

          <label className="flex items-center gap-2">
            📅 عدد الأيام:
            <input
              type="number"
              min="1"
              value={days}
              onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 1))}
              className="border p-1 rounded w-24 text-center"
            />
          </label>

          <label className="flex items-center gap-2">
            🗓️ تاريخ الإرجاع:
            <input
              type="date"
              value={dueDate}
              min={todayStr}
              onChange={handleDateChange}
              className="border p-1 rounded"
            />
          </label>

          <button
            type="submit"
            className="col-span-full bg-green-600 text-white py-2 rounded hover:bg-green-700 transition"
          >
            📥 تنفيذ الاستعارة
          </button>
        </motion.form>
      )}

      {/* وضع المسح */}
      {mode === 'scan' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white p-6 rounded-xl shadow mb-8 max-w-2xl mx-auto space-y-6"
        >
          {!scanUser ? (
            <>
              <p className="text-center text-lg font-medium text-gray-700">📷 امسح كود المستخدم:</p>

              {/* حاوية ماسح مرنة بنسبة 4:3 */}
              <div className="w-full max-w-md mx-auto rounded-lg overflow-hidden border">
                <div className="relative" style={{ paddingTop: '75%' }}>
                  <div className="absolute inset-0">
                    <QrScanner
                      delay={300}
                      style={previewStyle}
                      constraints={{ video: videoConstraints }}
                      onScan={handleScan}
                      onError={(err) => console.error('Scan error:', err)}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                <p className="text-sm sm:text-base">
                  <strong>👤 المستخدم:</strong> <span className="break-all">{scanUser}</span>
                </p>
                <button
                  onClick={() => {
                    setScanUser('');
                    setScanBooks([]);
                  }}
                  className="text-red-600 underline"
                >
                  إعادة التعيين
                </button>
              </div>

              <p className="text-center text-gray-700">📚 امسح رموز الكتب:</p>

              {/* حاوية الماسح للكتب */}
              <div className="w-full max-w-md mx-auto rounded-lg overflow-hidden border">
                <div className="relative" style={{ paddingTop: '75%' }}>
                  <div className="absolute inset-0">
                    <QrScanner
                      delay={300}
                      style={previewStyle}
                      constraints={{ video: videoConstraints }}
                      onScan={handleScan}
                      onError={(err) => console.error('Scan error:', err)}
                    />
                  </div>
                </div>
              </div>

              <ul className="list-disc pr-6 space-y-1">
                {scanBooks.map((b) => (
                  <li key={b._id} className="text-gray-800">
                    {b.name} – <span className="text-sm text-gray-600">{b.author}</span>
                  </li>
                ))}
              </ul>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="flex items-center gap-2">
                  📅 عدد الأيام:
                  <input
                    type="number"
                    min="1"
                    value={days}
                    onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 1))}
                    className="border p-1 rounded w-24 text-center"
                  />
                </label>

                <label className="flex items-center gap-2">
                  🗓️ تاريخ الإرجاع:
                  <input
                    type="date"
                    value={dueDate}
                    min={todayStr}
                    onChange={handleDateChange}
                    className="border p-1 rounded"
                  />
                </label>
              </div>

              <button
                onClick={handleConfirmScan}
                className="bg-green-600 text-white px-4 py-2 rounded mt-2 hover:bg-green-700 transition w-full sm:w-auto"
              >
                ✅ تأكيد الاستعارة
              </button>
            </>
          )}
        </motion.div>
      )}

      {/* السجلات */}
      <div>
        <h3 className="text-xl font-semibold mb-4 text-center">📄 سجلات الاستعارة</h3>

        {/* شبكة مرنة بدل سلايدر فقط */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {records.map((r) => (
            <div
              key={r._id}
              className="bg-gray-50 p-4 rounded-xl shadow text-gray-800 relative hover:shadow-md transition"
            >
              <p className="font-semibold">
                👤 {r.user?.name || 'مستخدم غير معروف'}
              </p>
              <p>📖 {r.book?.name || 'كتاب غير معروف'}</p>
              <p>🗓️ يرجع قبل: {r.dueDate ? new Date(r.dueDate).toLocaleDateString() : '—'}</p>

              <div className="mt-3 flex gap-4">
                <button
                  onClick={() => handleDeleteRecord(r._id)}
                  className="text-red-600 text-sm hover:underline"
                >
                  حذف
                </button>
                {r.user?._id && r.book?._id && (
                  <button
                    onClick={() => {
                      setMode('manual');
                      setManualUser(r.user._id);
                      setManualBook(r.book._id);
                      setDueDate(r.dueDate ? new Date(r.dueDate).toISOString().split('T')[0] : todayStr);
                    }}
                    className="text-blue-600 text-sm hover:underline"
                  >
                    تعديل
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
